import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, CheckCircle2, AlertTriangle } from 'lucide-react';
import './demo.css';

/* ═══════════════════════════════════════════════════════
   TIMELINE
   ═══════════════════════════════════════════════════════ */
const TOTAL = 60;

const SC = {
  HOOK:       { s: 0,    e: 7,    fi: 0 },
  PROBLEM:    { s: 7,    e: 15,   fi: 0.4 },
  TENSION:    { s: 15,   e: 27,   fi: 0.4 },
  BLACK1:     { s: 27,   e: 28.5, fi: 0 },
  RESOLUTION: { s: 28.5, e: 41.5, fi: 0.4 },
  PROOF:      { s: 41.5, e: 49.5, fi: 0.4 },
  BLACK2:     { s: 49.5, e: 50,   fi: 0 },
  CLOSE:      { s: 50,   e: 60,   fi: 0 },
};

/* ═══ Timeline Hook ═══ */
function useTimeline(duration, onEnd) {
  const [t, setT] = useState(0);
  const r = useRef({ start: null, raf: null, done: false });

  useEffect(() => {
    const state = r.current;
    state.start = performance.now();
    const tick = () => {
      const el = (performance.now() - state.start) / 1000;
      setT(el);
      if (el >= duration) {
        if (!state.done) { state.done = true; onEnd?.(); }
        return;
      }
      state.raf = requestAnimationFrame(tick);
    };
    state.raf = requestAnimationFrame(tick);
    return () => { if (state.raf) cancelAnimationFrame(state.raf); };
  }, [duration, onEnd]);

  return t;
}

/* ═══════════════════════════════════════════════════════
   VISUAL BACKGROUNDS
   ═══════════════════════════════════════════════════════ */

/* Scene 2 — AI Security Dashboard (blurred) */
function DashboardBG() {
  const cardStyle = (color) => ({
    background: 'rgba(15,22,41,0.8)',
    border: '1px solid #1E2A45',
    borderRadius: 12,
    padding: 20,
  });
  return (
    <div style={{ width: '100vw', height: '100vh', background: '#0A0A0F' }}>
      {/* Top bar */}
      <div style={{ height: 52, background: '#080D1A', borderBottom: '1px solid #1E2A45', display: 'flex', alignItems: 'center', padding: '0 24px', gap: 16 }}>
        <span style={{ color: '#6366F1', fontWeight: 700, fontSize: 14, fontFamily: "'IBM Plex Mono', monospace" }}>APEX</span>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', gap: 12 }}>
          {[1,2,3].map(i => <div key={i} style={{ width: 8, height: 8, borderRadius: '50%', background: '#1E2A45' }} />)}
        </div>
      </div>

      <div style={{ display: 'flex' }}>
        {/* Sidebar */}
        <div style={{ width: 220, background: '#080D1A', borderRight: '1px solid #1E2A45', padding: '20px 12px', height: 'calc(100vh - 52px)' }}>
          {[1,2,3,4,5,6,7,8].map(i => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, background: i === 1 ? 'rgba(99,102,241,0.08)' : 'transparent', marginBottom: 4 }}>
              <div style={{ width: 16, height: 16, borderRadius: 4, background: i === 1 ? '#6366F1' : '#1E2A45' }} />
              <div style={{ height: 7, width: 80 + Math.random() * 40, background: i === 1 ? '#94A3B8' : '#1E2A45', borderRadius: 4, opacity: 0.6 }} />
            </div>
          ))}
        </div>

        {/* Main content */}
        <div style={{ flex: 1, padding: '24px 32px', overflowY: 'hidden' }}>
          {/* Status line */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#10B981' }} />
            <div style={{ height: 8, width: 300, background: '#1E2A45', borderRadius: 4, opacity: 0.5 }} />
          </div>

          {/* Stat cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
            {[
              { accent: '#EF4444', val: '1', label: 'Active Exceptions' },
              { accent: '#F59E0B', val: '143', label: 'Decisions Affected' },
              { accent: '#F59E0B', val: 'SOC 2 PI1', label: 'Regulatory Exposure' },
              { accent: '#64748B', val: 'Apr 19', label: 'Resolution ETA' },
            ].map((c, i) => (
              <div key={i} style={cardStyle(c.accent)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: c.accent }} />
                  <span style={{ fontSize: 9, color: '#475569', textTransform: 'uppercase', letterSpacing: 1 }}>{c.label}</span>
                </div>
                <div style={{ fontSize: 22, fontWeight: 700, color: '#F8FAFC' }}>{c.val}</div>
                <div style={{ height: 5, width: 100, background: c.accent + '20', borderRadius: 3, marginTop: 8 }} />
              </div>
            ))}
          </div>

          {/* Alert banner */}
          <div style={{ background: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 12, padding: '14px 20px', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#EF4444' }} />
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#F8FAFC', marginBottom: 4 }}>ACTIVE INVESTIGATION</div>
              <div style={{ fontSize: 10, color: '#94A3B8' }}>Lineage Anomaly — Legal Document Drafting Workflow</div>
            </div>
          </div>

          {/* Chart + table */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16 }}>
            <div style={{ background: 'rgba(15,22,41,0.8)', border: '1px solid #1E2A45', borderRadius: 12, padding: 24, height: 200 }}>
              <div style={{ fontSize: 10, color: '#475569', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 16 }}>Decision Volume — 30 Days</div>
              <svg width="100%" height="130" viewBox="0 0 500 130" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="cg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#6366F1" stopOpacity="0.3" />
                    <stop offset="100%" stopColor="#6366F1" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <path d="M0,110 C50,100 80,85 120,90 C160,95 190,40 240,55 C290,70 310,30 360,45 C410,60 440,20 500,30 L500,130 L0,130 Z" fill="url(#cg)" />
                <path d="M0,110 C50,100 80,85 120,90 C160,95 190,40 240,55 C290,70 310,30 360,45 C410,60 440,20 500,30" fill="none" stroke="#6366F1" strokeWidth="2" />
              </svg>
            </div>
            <div style={{ background: 'rgba(15,22,41,0.8)', border: '1px solid #1E2A45', borderRadius: 12, padding: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ width: 100, height: 100, borderRadius: '50%', border: '8px solid #6366F1', borderTopColor: '#1E2A45', position: 'relative' }}>
                <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 700, color: '#F8FAFC' }}>87</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* Scene 3 — Failure DER: Blast Radius */
function FailureDERBG() {
  return (
    <div style={{ width: '100vw', height: '100vh', background: '#0A0A0F', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: '80%', maxWidth: 1000 }}>
        {/* DER Card */}
        <div style={{ background: 'rgba(15,22,41,0.8)', border: '1px solid #1E2A45', borderTop: '3px solid #F59E0B', borderRadius: 12, padding: '32px 40px' }}>
          {/* Header */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 28, fontWeight: 700, color: '#F8FAFC', textShadow: '0 0 20px rgba(245,158,11,0.15)', marginBottom: 12 }}>
              DER-2026-031204
            </div>
            <span style={{ fontSize: 11, fontFamily: "'IBM Plex Mono', monospace", padding: '4px 12px', borderRadius: 20, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)', color: '#F59E0B' }}>
              EVIDENCE INCOMPLETE · INTEGRITY WARNING DETECTED
            </span>
          </div>

          {/* Decision Significance */}
          <div style={{ borderLeft: '4px solid #F59E0B', background: 'rgba(245,158,11,0.03)', borderRadius: '0 8px 8px 0', padding: '16px 24px', marginBottom: 28 }}>
            <div style={{ fontSize: 9, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 8 }}>Decision Significance</div>
            <div style={{ fontSize: 12, color: '#94A3B8', lineHeight: 1.8 }}>
              <div><span style={{ color: '#F8FAFC', fontWeight: 600 }}>Risk Classification:</span> High-risk regulated workflow · active integrity warning</div>
              <div><span style={{ color: '#F8FAFC', fontWeight: 600 }}>Audit Impact:</span> Likely control exception under SOC 2 PI1 · processing integrity evidence incomplete</div>
            </div>
          </div>

          {/* BLAST RADIUS — the zoom target */}
          <div className="amber-pulse-box" style={{ borderLeft: '4px solid #F59E0B', background: 'rgba(245,158,11,0.04)', borderRadius: '0 12px 12px 0', padding: '24px 32px' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#F59E0B', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <AlertTriangle size={14} />
              Blast Radius & Related Records
            </div>
            <div className="amber-pulse-text" style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 18, color: '#F59E0B', lineHeight: 2 }}>
              <span style={{ fontSize: 32, fontWeight: 700 }}>143 decisions</span> share this upstream source ·{' '}
              <span style={{ fontWeight: 600 }}>27 records</span> use same model version ·{' '}
              <span style={{ fontWeight: 600 }}>3 audit packages</span> may contain affected evidence ·{' '}
              <span style={{ fontWeight: 600 }}>1 active exception</span> linked
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* Scene 5 — Resolved DER: Resolution Lifecycle */
function ResolvedDERBG({ glowIntensity }) {
  const stages = [
    { label: 'DETECTED', time: 'Apr 3, 12:41 UTC', detail: 'Evidence Vault integrity check' },
    { label: 'INVESTIGATED', time: 'Apr 4, 09:15 UTC', detail: 'IT Security opened investigation' },
    { label: 'REMEDIATION', time: 'Apr 7, 14:22 UTC', detail: 'External Legal Corpus re-verified' },
    { label: 'CLOSED', time: 'Apr 8, 10:05 UTC', detail: 'Control re-tested · PASS' },
  ];

  return (
    <div style={{ width: '100vw', height: '100vh', background: '#0A0A0F', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: '80%', maxWidth: 1000 }}>
        {/* DER Card */}
        <div style={{ background: 'rgba(15,22,41,0.8)', border: '1px solid #1E2A45', borderTop: '3px solid #10B981', borderRadius: 12, padding: '32px 40px' }}>
          {/* Header */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 24, fontWeight: 700, color: '#F8FAFC', marginBottom: 8 }}>
              DER-2026-028847
            </div>
            <span style={{ fontSize: 11, fontFamily: "'IBM Plex Mono', monospace", padding: '4px 12px', borderRadius: 20, background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)', color: '#10B981' }}>
              EVIDENCE COMPLETE · CONTROL RE-VERIFIED · EXCEPTION CLOSED
            </span>
          </div>

          {/* Resolution header */}
          <div style={{ fontSize: 11, fontWeight: 600, color: '#F8FAFC', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 12 }}>
            Resolution Lifecycle
            <span style={{ fontSize: 10, fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: 'rgba(16,185,129,0.8)' }}>
              Audit package ready · Exported Apr 19, 2026
            </span>
          </div>

          {/* GREEN PAYOFF BANNER — zoom target */}
          <div style={{
            borderLeft: '3px solid #10B981',
            background: `rgba(16,185,129,${0.06 + glowIntensity * 0.12})`,
            borderRadius: '0 12px 12px 0',
            padding: '16px 24px',
            marginBottom: 28,
            display: 'flex', alignItems: 'center', gap: 12,
            boxShadow: `0 0 ${20 + glowIntensity * 60}px rgba(16,185,129,${0.05 + glowIntensity * 0.25}), inset 0 0 ${10 + glowIntensity * 30}px rgba(16,185,129,${0.02 + glowIntensity * 0.08})`,
            transition: 'box-shadow 0.3s ease',
          }}>
            <Shield size={20} style={{ color: '#10B981', flexShrink: 0 }} />
            <span style={{ fontSize: 16, fontWeight: 600, color: '#10B981' }}>
              Risk eliminated · Detection to closure: 4 days · Within audit SLA · Controls restored: 9/9 · SOC 2 PI1 re-tested: PASS
            </span>
          </div>

          {/* Contrast line */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, color: 'rgba(148,163,184,0.7)', fontStyle: 'italic', marginBottom: 32 }}>
            <AlertTriangle size={10} style={{ color: '#F59E0B', flexShrink: 0 }} />
            Previously: integrity warning · 8,203 decisions affected · External Legal Corpus unverified
          </div>

          {/* Lifecycle steps */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 0 }}>
            {stages.map((stage, i) => (
              <div key={stage.label} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', position: 'relative' }}>
                {/* Connector line */}
                {i < stages.length - 1 && (
                  <div style={{ position: 'absolute', top: 20, left: '50%', width: '100%', height: 2, background: '#10B981', opacity: 0.4, zIndex: 0 }} />
                )}
                <div style={{
                  width: 40, height: 40, borderRadius: '50%',
                  background: 'rgba(16,185,129,0.15)', border: '2px solid #10B981',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  zIndex: 1, position: 'relative',
                }}>
                  <CheckCircle2 size={16} style={{ color: '#10B981' }} />
                </div>
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#10B981', letterSpacing: 1, textTransform: 'uppercase' }}>{stage.label}</div>
                  <div style={{ fontSize: 9, color: '#64748B', fontFamily: "'IBM Plex Mono', monospace", marginTop: 4 }}>{stage.time}</div>
                  <div style={{ fontSize: 9, color: '#94A3B8', marginTop: 2 }}>{stage.detail}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom summary */}
          <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid #1E2A45', fontSize: 10, color: '#94A3B8' }}>
            End-to-end evidence package generated automatically · No manual assembly required
          </div>
        </div>
      </div>
    </div>
  );
}

/* Scene 6 — Assurance Center: SOC 2 Card */
function AssuranceBG() {
  const packages = [
    { title: 'Regulator Package (EU AI Act)', pct: 68, color: '#F59E0B', dim: true },
    { title: 'SOC 2 AI Attestation Package', pct: 61, color: '#6366F1', dim: false },
    { title: 'Board Risk Committee Pack', pct: 94, color: '#10B981', dim: true },
  ];

  return (
    <div style={{ width: '100vw', height: '100vh', background: '#0A0A0F', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: '88%', maxWidth: 1200 }}>
        <div style={{ fontSize: 10, color: '#94A3B8', marginBottom: 24 }}>
          Generate regulator-ready evidence packages for auditors, insurers, and board committees.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
          {packages.map((pkg) => (
            <div
              key={pkg.title}
              className={!pkg.dim ? 'indigo-pulse-box' : ''}
              style={{
                background: 'rgba(15,22,41,0.8)',
                border: `1px solid ${pkg.dim ? '#1E2A45' : '#6366F1'}`,
                borderRadius: 12,
                padding: 28,
                opacity: pkg.dim ? 0.4 : 1,
                transition: 'opacity 0.3s',
              }}
            >
              {/* Ring */}
              <div style={{ position: 'relative', width: 80, height: 80, margin: '0 auto 16px' }}>
                <svg width="80" height="80" viewBox="0 0 80 80">
                  <circle cx="40" cy="40" r="32" fill="none" stroke="#1E2A45" strokeWidth="6" />
                  <circle
                    cx="40" cy="40" r="32" fill="none"
                    stroke={pkg.color} strokeWidth="6"
                    strokeDasharray={`${pkg.pct * 2.01} ${201 - pkg.pct * 2.01}`}
                    strokeDashoffset="50"
                    strokeLinecap="round"
                  />
                </svg>
                <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 700, color: '#F8FAFC' }}>
                  {pkg.pct}
                </span>
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#F8FAFC', textAlign: 'center', marginBottom: 8 }}>
                {pkg.title}
              </div>
              {!pkg.dim && (
                <div style={{ fontSize: 11, color: '#94A3B8', textAlign: 'center', lineHeight: 1.6 }}>
                  AI-specific evidence inputs for SOC 2 Type II examinations. Supports Processing Integrity, Security, and Confidentiality trust service criteria.
                  <div style={{ fontSize: 9, color: '#475569', marginTop: 8, fontStyle: 'italic' }}>
                    Apex provides AI evidence inputs. Your licensed CPA firm issues the SOC 2 opinion.
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════ */
export default function CISODemo() {
  const nav = useNavigate();
  const onEnd = useCallback(() => nav('/demo'), [nav]);
  const t = useTimeline(TOTAL, onEnd);

  /* ─── Helpers ─── */
  const fade = (at, d = 0.5) => Math.min(1, Math.max(0, (t - at) / d));
  const prog = (a, b) => Math.min(1, Math.max(0, (t - a) / (b - a)));

  const lOp = (sc) => {
    if (sc.fi === 0) return t >= sc.s && t < sc.e ? 1 : 0;
    const fs = sc.s - sc.fi;
    if (t < fs) return 0;
    if (t < sc.s) return (t - fs) / sc.fi;
    if (t < sc.e) return 1;
    return 0;
  };

  /* ─── Green glow intensity for Scene 5 (builds over 4s) ─── */
  const greenGlow = SC.RESOLUTION.fi
    ? Math.min(1, Math.max(0, (t - SC.RESOLUTION.s) / 4))
    : Math.min(1, Math.max(0, (t - SC.RESOLUTION.s) / 4));

  /* ─── Close scene text fade out ─── */
  const closeTextOp = (() => {
    const textEnd = 54.1; // 3s after last line (51.1)
    const fadeOutEnd = 56;
    if (t < textEnd) return 1;
    if (t < fadeOutEnd) return 1 - (t - textEnd) / (fadeOutEnd - textEnd);
    return 0;
  })();

  return (
    <div className="demo-root" data-testid="ciso-demo">

      {/* ═══ S1 — HOOK (0-7) ═══ */}
      <div className="demo-layer-flex" style={{ opacity: lOp(SC.HOOK), zIndex: 1 }}>
        <div className="demo-center-stack">
          <p className="demo-hero" style={{ opacity: fade(0.5, 0.6) }}>
            Last week, your AI made thousands of decisions.
          </p>
          <p className="demo-hero" style={{ opacity: fade(1.1, 0.5), marginTop: 16 }}>
            Can you prove how each system was classified when it acted?
          </p>
          <p className="demo-hero" style={{ opacity: fade(1.5, 0.5), marginTop: 12 }}>
            Which controls were active at that exact moment?
          </p>
          <p className="demo-hero" style={{ opacity: fade(1.9, 0.5), marginTop: 12 }}>
            And whether human oversight signals were defensible?
          </p>
          <p className="demo-hero" style={{ opacity: fade(4.5, 0.6), marginTop: 24 }}>
            Most organizations can't answer those questions.
          </p>
          <p className="demo-hero demo-text-accent" style={{ opacity: fade(5.0, 0.5), marginTop: 12 }}>
            Apex Trust can — with a signed proof chain.
          </p>
        </div>
      </div>

      {/* ═══ S2 — PROBLEM (7-15) ═══ */}
      <div className="demo-layer" style={{ opacity: lOp(SC.PROBLEM), zIndex: 2 }}>
        {/* Blurred dashboard */}
        <div className="demo-bg-fill" style={{
          filter: 'blur(8px)',
          opacity: 0.6,
          transform: `scale(${1 + prog(SC.PROBLEM.s, SC.PROBLEM.e) * 0.05})`,
        }}>
          <DashboardBG />
        </div>
        {/* Text overlays — left-aligned */}
        <div className="demo-overlay-left">
          <p className="demo-text-overlay" style={{ opacity: fade(7.8) }}>You can't.</p>
          <p className="demo-text-overlay" style={{ opacity: fade(8.3) }}>You have logs.</p>
          <p className="demo-text-overlay" style={{ opacity: fade(8.7) }}>Policies.</p>
          <p className="demo-text-overlay" style={{ opacity: fade(9.1) }}>Dashboards.</p>
          <p className="demo-text-overlay demo-text-accent" style={{ opacity: fade(9.7) }}>
            But no proof-grade evidence.
          </p>
        </div>
      </div>

      {/* ═══ S3 — TENSION (15-27) ═══ */}
      <div className="demo-layer" style={{ opacity: lOp(SC.TENSION), zIndex: 3 }}>
        <div className="demo-bg-fill" style={{
          transform: `scale(${1 + prog(SC.TENSION.s, SC.TENSION.e) * 1.2})`,
          transformOrigin: '50% 68%',
        }}>
          <FailureDERBG />
        </div>
        {/* Text overlays — bottom center */}
        <div className="demo-overlay-bottom">
          <p className="demo-text-overlay" style={{ opacity: fade(16.0) }}>One unverified data source…</p>
          <p className="demo-text-overlay demo-text-accent" style={{ opacity: fade(16.5) }}>143 decisions affected.</p>
          <p className="demo-text-overlay" style={{ opacity: fade(17.3) }}>You only find out during an audit…</p>
          <p className="demo-text-overlay" style={{ opacity: fade(17.8) }}>Or after something breaks.</p>
        </div>
      </div>

      {/* ═══ S4 — BLACK TRANSITION (27-28.5) ═══ */}
      <div className="demo-layer" style={{ opacity: lOp(SC.BLACK1), zIndex: 4, background: '#0A0A0F' }} />

      {/* ═══ S5 — RESOLUTION (28.5-41.5) ═══ */}
      <div className="demo-layer" style={{ opacity: lOp(SC.RESOLUTION), zIndex: 5 }}>
        <div className="demo-bg-fill" style={{
          transform: `scale(${1 + prog(SC.RESOLUTION.s, SC.RESOLUTION.e) * 0.08})`,
          transformOrigin: '50% 38%',
        }}>
          <ResolvedDERBG glowIntensity={greenGlow} />
        </div>
        {/* Text overlays */}
        <div className="demo-overlay-bottom">
          <p className="demo-text-overlay" style={{ opacity: fade(29.5) }}>
            Apex captures governed AI decisions where it's deployed in-line…
          </p>
          <p className="demo-text-overlay" style={{ opacity: fade(30.0) }}>
            Reconstructs what happened…
          </p>
          <p className="demo-text-overlay" style={{ opacity: fade(30.5) }}>
            And proves you were in control.
          </p>
          <p className="demo-text-overlay demo-text-accent" style={{ opacity: fade(31.3) }}>
            Risk contained.
          </p>
          <p className="demo-text-overlay demo-text-accent" style={{ opacity: fade(31.8) }}>
            Controls restored.
          </p>
          <p className="demo-text-overlay demo-text-accent" style={{ opacity: fade(32.3), color: '#10B981', fontWeight: 700 }}>
            Audit-ready.
          </p>
        </div>
      </div>

      {/* ═══ S6 — PROOF (41.5-49.5) ═══ */}
      <div className="demo-layer" style={{ opacity: lOp(SC.PROOF), zIndex: 6 }}>
        <div className="demo-bg-fill" style={{
          transform: `scale(${1 + prog(SC.PROOF.s, SC.PROOF.e) * 0.06})`,
          transformOrigin: '50% 50%',
        }}>
          <AssuranceBG />
        </div>
        {/* Text overlays */}
        <div className="demo-overlay-bottom">
          <p className="demo-text-overlay" style={{ opacity: fade(42.0) }}>Evidence packages generated automatically.</p>
          <p className="demo-text-overlay" style={{ opacity: fade(42.5) }}>For auditors.</p>
          <p className="demo-text-overlay" style={{ opacity: fade(42.9) }}>For regulators.</p>
          <p className="demo-text-overlay" style={{ opacity: fade(43.3) }}>For insurers.</p>
          <p className="demo-text-overlay demo-text-accent" style={{ opacity: fade(43.9) }}>No manual assembly.</p>
        </div>
      </div>

      {/* ═══ S6.5 — BLACK DECOMPRESSION (49.5-50) ═══ */}
      <div className="demo-layer" style={{ opacity: lOp(SC.BLACK2), zIndex: 7, background: '#0A0A0F' }} />

      {/* ═══ S7 — CLOSE (50-60) ═══ */}
      <div className="demo-layer-flex" style={{ opacity: lOp(SC.CLOSE), zIndex: 8 }}>
        <div className="demo-center-stack" style={{ opacity: closeTextOp }}>
          <p className="demo-hero-large" style={{ opacity: fade(50.5, 0.6), maxWidth: 1000, fontSize: '1.8rem' }}>
            Apex Trust proves what your AI was classified as, what controlled it at decision time, what it decided, and how it was reviewed.
          </p>
          <p className="demo-hero-large" style={{ opacity: fade(52.0, 0.5), marginTop: 12, fontSize: '1.5rem', color: '#94A3B8' }}>
            With a signed proof chain designed for audit and regulatory scrutiny.
          </p>
          <p className="demo-hero-large" style={{ opacity: fade(53.0, 0.5), marginTop: 20 }}>
            First proof chain in seven days.
          </p>
        </div>
      </div>
    </div>
  );
}
